/**
 * Hooks React Query para o módulo de Clientes (CRM — Plano C, Task 4).
 *
 * CRUD completo + associate/disassociate de contato. Create/update NÃO
 * toastam erro automaticamente — `customersService` marca essas chamadas com
 * `_handleErrorLocally` (suprime o toast do interceptor global) porque
 * `CustomerFormPage` trata o 409 de documento duplicado como erro de campo;
 * os demais mutations (delete, associate, disassociate) toastam normalmente.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  CreateCustomerRequest,
  CustomerResponse,
  CustomerServiceListResponse,
  CustomerServiceQuery,
  UpdateCustomerRequest,
} from '@troia-v3/types';

import { customersService } from '../services';

/** Mensagem real do backend (`{ success: false, message }`) — fallback genérico se ausente. */
function extractErrorMessage(error: unknown, fallback: string): string {
  const maybeAxios = error as { response?: { data?: { message?: string } } } | undefined;
  return maybeAxios?.response?.data?.message || fallback;
}

/** Lista paginada de clientes — `GET /customers` (busca + filtros). */
export function useCustomers(query?: CustomerServiceQuery, options?: { enabled?: boolean }) {
  return useQuery<CustomerServiceListResponse>({
    queryKey: ['customers', query],
    queryFn: () => customersService.getCustomers(query),
    staleTime: 30 * 1000,
    enabled: options?.enabled ?? true,
  });
}

/** Cliente por id — `GET /customers/:id`. Só busca quando `id` está definido. */
export function useCustomer(id?: string, options?: { enabled?: boolean }) {
  return useQuery<CustomerResponse>({
    queryKey: ['customer', id],
    queryFn: () => customersService.getCustomerById(id!),
    enabled: (options?.enabled ?? true) && !!id,
    staleTime: 30 * 1000,
  });
}

/**
 * Criar cliente. Sem `onError` — 409 (documento duplicado) e demais erros
 * são tratados no `CustomerFormPage` via `catch` do `mutateAsync`.
 *
 * `options.silent` (default `false`) suprime o toast de sucesso — usado pelo
 * `QuickCreateCustomerDialog` (atalho do header do chat), que já dispara seu
 * PRÓPRIO toast de "criado e vinculado" logo em seguida via
 * `useSetContactCustomer`. Sem o silenciamento os dois empilhavam (correção
 * 2026-08-20). Os demais consumidores (ex.: `CustomerFormPage`) continuam com
 * o toast normal — não passar a opção mantém o comportamento de sempre.
 */
export function useCreateCustomer(options?: { silent?: boolean }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCustomerRequest) => customersService.createCustomer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      if (!options?.silent) {
        toast.success('Cliente criado com sucesso');
      }
    },
  });
}

/** Atualizar cliente. Mesmo tratamento local de erro do create. */
export function useUpdateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCustomerRequest }) =>
      customersService.updateCustomer(id, data),
    onSuccess: (customer, variables) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customer', variables.id] });
      if (customer?.id) {
        queryClient.setQueryData(['customer', customer.id], customer);
      }
      toast.success('Cliente atualizado com sucesso');
    },
  });
}

/** Excluir cliente (soft delete). */
export function useDeleteCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => customersService.deleteCustomer(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success('Cliente excluído com sucesso');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'Erro ao excluir cliente'));
    },
  });
}

/** Vincula um contato existente ao cliente (`POST /customers/:id/contacts`). */
export function useAssociateCustomerContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ customerId, contactId }: { customerId: string; contactId: string }) =>
      customersService.associateContact(customerId, contactId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['customer', variables.customerId] });
      toast.success('Contato vinculado ao cliente');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'Erro ao vincular contato'));
    },
  });
}

/** Desvincula um contato do cliente (`DELETE /customers/:id/contacts/:contactId`). */
export function useDisassociateCustomerContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ customerId, contactId }: { customerId: string; contactId: string }) =>
      customersService.disassociateContact(customerId, contactId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['customer', variables.customerId] });
      toast.success('Contato desvinculado do cliente');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'Erro ao desvincular contato'));
    },
  });
}
